import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Plus, PhoneCall, Loader2, CheckCircle2, RotateCcw, Star, Search,
  Phone, MessageCircle, Mail, MessageSquare, Users as UsersIcon, Sparkles,
  MoreHorizontal, Lock, Unlock, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { chainOutreachApi } from '../services/api';

const CHANNELS = [
  { value: 'call', label: 'Call', Icon: Phone, tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  { value: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, tone: 'text-green-700 bg-green-50 border-green-200' },
  { value: 'sms', label: 'SMS', Icon: MessageSquare, tone: 'text-sky-700 bg-sky-50 border-sky-200' },
  { value: 'email', label: 'Email', Icon: Mail, tone: 'text-blue-700 bg-blue-50 border-blue-200' },
  { value: 'in_person', label: 'In-person', Icon: UsersIcon, tone: 'text-purple-700 bg-purple-50 border-purple-200' },
  { value: 'other', label: 'Other', Icon: MoreHorizontal, tone: 'text-slate-700 bg-slate-50 border-slate-200' },
];
const channelMeta = (v) => CHANNELS.find(c => c.value === v) || CHANNELS[CHANNELS.length - 1];

// ─── Create Outreach Dialog ─────────────────────────────────────────────────
const CreateOutreachDialog = ({ open, onOpenChange, allCustomers, onCreated }) => {
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [includeAllChampions, setIncludeAllChampions] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(''); setPurpose(''); setIncludeAllChampions(true);
      setSelected(new Set()); setQ('');
    }
  }, [open]);

  const champions = useMemo(() => allCustomers.filter(c => c.is_champion), [allCustomers]);
  const nonChampionMatches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return allCustomers
      .filter(c => !c.is_champion)
      .filter(c =>
        (c.company_name || '').toLowerCase().includes(s)
        || (c.contact_person_name || '').toLowerCase().includes(s)
        || (c.mobile || '').includes(s)
        || (c.additional_phones || []).some(p =>
          (p?.name || '').toLowerCase().includes(s)
          || (p?.label || '').toLowerCase().includes(s)
          || (p?.phone || '').includes(s)
        )
      )
      .slice(0, 25);
  }, [allCustomers, q]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const totalPicked = (includeAllChampions ? champions.length : 0)
    + Array.from(selected).filter(id => {
      const c = allCustomers.find(x => x.id === id);
      // if include-all-champions is on and this one is a champion, don't double-count
      if (includeAllChampions && c?.is_champion) return false;
      return true;
    }).length;

  const submit = async () => {
    if (!title.trim()) { toast.error('Give your outreach a title'); return; }
    if (totalPicked === 0) { toast.error('Pick at least one customer'); return; }
    setSaving(true);
    try {
      const r = await chainOutreachApi.create({
        title: title.trim(),
        purpose: purpose.trim(),
        include_all_champions: includeAllChampions,
        customer_ids: Array.from(selected),
      });
      toast.success(`Outreach "${r.data.title}" created — ${r.data.progress.total} customers`);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create outreach');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="create-outreach-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4 text-amber-600" /> New Chain Outreach
          </DialogTitle>
          <DialogDescription>
            Line up a list of customers to call/message about an exhibition, launch or follow-up.
            Rows you mark done also get auto-logged to the customer&apos;s Activity feed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Title *</Label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Nov Exhibition invite"
              className="h-11 mt-1"
              data-testid="outreach-title"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Purpose / brief</Label>
            <Textarea
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
              placeholder="What are we telling them? Talking points…"
              rows={3}
              className="mt-1"
              data-testid="outreach-purpose"
            />
          </div>

          <div className="rounded-lg border p-3 bg-amber-50/50 dark:bg-amber-500/5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={includeAllChampions}
                onCheckedChange={(v) => setIncludeAllChampions(!!v)}
                data-testid="include-champions"
              />
              <div className="flex-1">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                  Include all Champion customers
                  <Badge variant="outline" className="text-[10px] ml-1">{champions.length}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Auto-adds every customer flagged as Champion. Toggle off for a fully manual list.
                </p>
              </div>
            </label>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Add other customers ({selected.size} selected)
            </Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search by company, contact or phone…"
                className="pl-9 h-10"
                data-testid="outreach-search"
              />
            </div>
            {selected.size > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {Array.from(selected).map(id => {
                  const c = allCustomers.find(x => x.id === id);
                  if (!c) return null;
                  return (
                    <Badge
                      key={id}
                      variant="outline"
                      className="text-[11px] pl-2 pr-1 py-0.5 gap-1"
                      data-testid={`picked-${id}`}
                    >
                      {c.company_name}
                      <button
                        onClick={() => toggle(id)}
                        className="hover:bg-muted rounded p-0.5"
                        aria-label="Remove"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
            {q.trim() && (
              <div className="mt-2 border rounded-lg max-h-56 overflow-y-auto divide-y">
                {nonChampionMatches.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground">No matches.</div>
                ) : nonChampionMatches.map(c => {
                  const picked = selected.has(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggle(c.id)}
                      className={cn(
                        "w-full text-left p-2.5 flex items-center gap-2 hover:bg-muted/50 transition-colors",
                        picked && "bg-emerald-50 dark:bg-emerald-500/10"
                      )}
                      data-testid={`pick-${c.id}`}
                    >
                      <Checkbox checked={picked} className="pointer-events-none" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.company_name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {c.contact_person_name || '—'} · {c.mobile || 'no phone'}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-lg bg-slate-50 dark:bg-neutral-900 border p-2.5 text-xs">
            Will create outreach with <strong>{totalPicked}</strong> customer{totalPicked === 1 ? '' : 's'}.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !title.trim() || totalPicked === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="submit-outreach"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
            Create Outreach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Mark-Done Dialog for one row ───────────────────────────────────────────
const MarkDoneDialog = ({ open, onOpenChange, outreachId, row, allCustomers, onDone }) => {
  const [channel, setChannel] = useState('call');
  const [contactId, setContactId] = useState('primary');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const customer = useMemo(
    () => allCustomers.find(c => c.id === row?.customer_id),
    [allCustomers, row?.customer_id],
  );

  const contactChoices = useMemo(() => {
    if (!customer) return [];
    const rows = [];
    if (customer.mobile) {
      rows.push({
        id: 'primary',
        name: customer.contact_person_name || 'Primary contact',
        phone: customer.mobile,
      });
    }
    (customer.additional_phones || []).forEach((p, idx) => {
      rows.push({
        id: p.phone || `add-${idx}`,
        name: p.name || p.label || 'Contact',
        phone: p.phone || '',
      });
    });
    rows.push({ id: 'manual', name: 'Someone else / manual entry…', phone: '' });
    return rows;
  }, [customer]);

  useEffect(() => {
    if (open) {
      setChannel('call');
      // Pick the first real contact (or 'manual' if there are literally none) — never
      // leave the Select on a value that no SelectItem exposes (e.g. 'primary' when the
      // customer has no mobile on file).
      const first = (contactChoices[0] && contactChoices[0].id) || 'manual';
      setContactId(first);
      setContactName(''); setContactPhone(''); setNotes(''); setSaving(false);
    }
  }, [open, row?.id, contactChoices]);

  useEffect(() => {
    const c = contactChoices.find(x => x.id === contactId);
    if (c && c.id !== 'manual') {
      setContactName(c.name || '');
      setContactPhone(c.phone || '');
    } else if (c?.id === 'manual') {
      setContactName(''); setContactPhone('');
    }
  }, [contactId, contactChoices]);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await chainOutreachApi.markRow(outreachId, row.id, {
        contact_id: contactId === 'manual' ? '' : contactId,
        contact_name: contactName.trim(),
        contact_phone: contactPhone.trim(),
        channel,
        notes: notes.trim(),
      });
      toast.success('Marked done — logged to customer activity');
      onDone?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark row');
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-md" data-testid="mark-done-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {row.customer_name || 'Customer'}
          </DialogTitle>
          <DialogDescription>Log how the conversation went</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Channel</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {CHANNELS.map(ch => {
                const on = channel === ch.value;
                const Icon = ch.Icon;
                return (
                  <button
                    key={ch.value}
                    type="button"
                    onClick={() => setChannel(ch.value)}
                    className={cn(
                      "flex items-center justify-center gap-1 px-2 py-2 rounded-lg border text-xs font-medium transition-colors",
                      on ? ch.tone : "hover:bg-muted"
                    )}
                    data-testid={`channel-${ch.value}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {ch.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Spoke to</Label>
            <Select value={contactId} onValueChange={setContactId}>
              <SelectTrigger className="h-10 mt-1" data-testid="contact-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {contactChoices.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}{c.phone ? ` · ${c.phone}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {contactId === 'manual' && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input
                  value={contactName}
                  onChange={e => setContactName(e.target.value)}
                  placeholder="Name"
                  className="h-10"
                  data-testid="manual-contact-name"
                />
                <Input
                  value={contactPhone}
                  onChange={e => setContactPhone(e.target.value)}
                  placeholder="Phone"
                  className="h-10 font-mono"
                  data-testid="manual-contact-phone"
                />
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="What did they say? Any commitments or next steps…"
              className="mt-1"
              data-testid="notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="confirm-mark-done"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
            Mark Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Outreach detail (rows table) ───────────────────────────────────────────
const OutreachDetail = ({ outreach, allCustomers, onChanged, onBack }) => {
  const [markRow, setMarkRow] = useState(null);
  const [busy, setBusy] = useState(false);

  const rows = outreach.customers || [];
  const done = rows.filter(r => r.status === 'done').length;
  const pct = rows.length ? Math.round((done / rows.length) * 100) : 0;

  const undo = async (row) => {
    if (!window.confirm(`Undo the log for ${row.customer_name}? This won't remove the activity entry.`)) return;
    setBusy(true);
    try {
      const r = await chainOutreachApi.undoRow(outreach.id, row.id);
      onChanged?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to undo');
    } finally { setBusy(false); }
  };

  const toggleClose = async () => {
    setBusy(true);
    try {
      if (outreach.status === 'closed') {
        await chainOutreachApi.reopen(outreach.id);
        toast.success('Outreach reopened');
      } else {
        await chainOutreachApi.close(outreach.id);
        toast.success('Outreach closed');
      }
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete outreach "${outreach.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await chainOutreachApi.delete(outreach.id);
      toast.success('Outreach deleted');
      onBack();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
      setBusy(false);
    }
  };

  return (
    <Card data-testid={`outreach-detail-${outreach.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <Button variant="ghost" size="sm" onClick={onBack} className="h-7 px-2 -ml-2 mb-1" data-testid="back-to-outreaches">
              ← All outreaches
            </Button>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <PhoneCall className="h-4 w-4 text-amber-600" /> {outreach.title}
              {outreach.status === 'closed' && (
                <Badge variant="outline" className="text-[10px] border-slate-300">Closed</Badge>
              )}
            </CardTitle>
            {outreach.purpose && (
              <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{outreach.purpose}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={toggleClose} disabled={busy} data-testid="toggle-close-outreach">
              {outreach.status === 'closed' ? (<><Unlock className="h-3.5 w-3.5 mr-1.5" /> Reopen</>) : (<><Lock className="h-3.5 w-3.5 mr-1.5" /> Close</>)}
            </Button>
            <Button size="sm" variant="ghost" onClick={remove} disabled={busy} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="delete-outreach">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-muted-foreground">{done} of {rows.length} done</span>
            <span className="font-semibold">{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 dark:bg-neutral-800 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="divide-y">
          {rows.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">No customers in this outreach.</div>
          )}
          {rows.map(row => {
            const isDone = row.status === 'done';
            const ch = channelMeta(row.channel);
            const ChIcon = ch.Icon;
            return (
              <div
                key={row.id}
                className={cn(
                  "px-3 md:px-4 py-3 flex items-center gap-3 flex-wrap sm:flex-nowrap",
                  isDone && "bg-emerald-50/30 dark:bg-emerald-500/5"
                )}
                data-testid={`outreach-row-${row.customer_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{row.customer_name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {row.customer_primary_mobile || 'no phone on file'}
                    {isDone && row.contact_name && ` · Spoke to ${row.contact_name}`}
                  </p>
                  {isDone && row.notes && (
                    <p className="text-xs mt-1 whitespace-pre-wrap text-slate-600 dark:text-slate-300">
                      {row.notes}
                    </p>
                  )}
                </div>

                {isDone ? (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className={cn('text-[10px]', ch.tone)}>
                      <ChIcon className="h-3 w-3 mr-1" /> {ch.label}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => undo(row)}
                      disabled={busy || outreach.status === 'closed'}
                      className="h-8 px-2"
                      data-testid={`undo-row-${row.customer_id}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setMarkRow(row)}
                    disabled={outreach.status === 'closed'}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                    data-testid={`mark-done-${row.customer_id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Mark done
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>

      <MarkDoneDialog
        open={!!markRow}
        onOpenChange={(v) => !v && setMarkRow(null)}
        outreachId={outreach.id}
        row={markRow}
        allCustomers={allCustomers}
        onDone={onChanged}
      />
    </Card>
  );
};

// ─── Main Chain Outreach section ────────────────────────────────────────────
const ChainOutreachSection = ({ allCustomers = [] }) => {
  const [outreaches, setOutreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await chainOutreachApi.list();
      setOutreaches(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load outreaches');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reloadOne = async (updated) => {
    // If server returned the freshest outreach, splice it in — else refetch list.
    if (updated?.id) {
      setOutreaches(prev => prev.map(o => o.id === updated.id ? updated : o));
    } else {
      await load();
    }
  };

  const activeCount = outreaches.filter(o => o.status !== 'closed').length;
  const closedCount = outreaches.filter(o => o.status === 'closed').length;
  const totalDoneAcrossActive = outreaches
    .filter(o => o.status !== 'closed')
    .reduce((a, o) => a + (o.progress?.done || 0), 0);
  const totalPendingAcrossActive = outreaches
    .filter(o => o.status !== 'closed')
    .reduce((a, o) => a + (o.progress?.pending || 0), 0);

  const selected = outreaches.find(o => o.id === selectedId);

  if (selected) {
    return (
      <OutreachDetail
        outreach={selected}
        allCustomers={allCustomers}
        onChanged={reloadOne}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="chain-outreach-section">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <PhoneCall className="h-4 w-4 text-amber-700 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-base md:text-lg font-bold tracking-tight">Chain Outreach</h3>
            <p className="text-xs text-muted-foreground">
              Mini CRM to reach out to Champion customers in one push
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="new-outreach-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Outreach
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Tile label="Active" value={activeCount} accent="text-amber-600" bg="bg-amber-50 dark:bg-amber-500/10" testid="tile-active" />
        <Tile label="Closed" value={closedCount} accent="text-slate-600" bg="bg-slate-50 dark:bg-neutral-800/50" testid="tile-closed" />
        <Tile label="Done (active)" value={totalDoneAcrossActive} accent="text-emerald-600" bg="bg-emerald-50 dark:bg-emerald-500/10" testid="tile-done" />
        <Tile label="Pending (active)" value={totalPendingAcrossActive} accent="text-rose-600" bg="bg-rose-50 dark:bg-rose-500/10" testid="tile-pending" />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
      ) : outreaches.length === 0 ? (
        <div className="text-center py-14 rounded-2xl bg-white dark:bg-neutral-900 border border-dashed">
          <Sparkles className="h-8 w-8 mx-auto mb-2 text-amber-500" />
          <p className="text-sm font-medium">No outreaches yet</p>
          <p className="text-xs text-muted-foreground mt-1">Kick one off to invite Champions to your next exhibition or launch.</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-4"
            onClick={() => setCreateOpen(true)}
            data-testid="new-outreach-empty-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Create first outreach
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border overflow-hidden">
          <div className="divide-y">
            {outreaches.map(o => {
              const p = o.progress || { total: 0, done: 0, pending: 0 };
              const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setSelectedId(o.id)}
                  className="w-full text-left px-3 md:px-5 py-3.5 hover:bg-amber-50/40 dark:hover:bg-amber-500/5 transition-colors flex items-center gap-3 flex-wrap sm:flex-nowrap"
                  data-testid={`outreach-card-${o.id}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <PhoneCall className="h-4 w-4 text-amber-700 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-semibold truncate">{o.title}</p>
                      {o.status === 'closed' && (
                        <Badge variant="outline" className="text-[10px] border-slate-300">Closed</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {p.done}/{p.total} done · {o.created_by_name || 'unknown'} · {new Date(o.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {/* Compact progress */}
                  <div className="w-24 sm:w-32 flex-shrink-0">
                    <div className="flex items-center justify-between text-[10px] mb-0.5">
                      <span className="text-muted-foreground">{pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-neutral-800 overflow-hidden">
                      <div
                        className={cn("h-full transition-all", pct === 100 ? "bg-emerald-500" : "bg-amber-500")}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <CreateOutreachDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        allCustomers={allCustomers}
        onCreated={() => load()}
      />
    </div>
  );
};

const Tile = ({ label, value, accent, bg, testid }) => (
  <div className={cn("p-3 rounded-xl border shadow-sm", bg)} data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
    <p className={cn("mt-0.5 text-lg md:text-xl font-bold tabular-nums", accent)}>{value}</p>
  </div>
);

export default ChainOutreachSection;
